import type { ErrorBody } from '@/bindings/ErrorBody'
import type { ErrorCode } from '@/bindings/ErrorCode'
import type { MeResponse } from '@/bindings/MeResponse'
import { type System, type UserOf, clearAuth, getAuth, setAuthenticated } from '@/lib/auth-store'

const BASE_URL = '/api'
const CSRF_HEADER = 'X-CSRF-Token'
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

/** Every API failure: the envelope's `code` is the i18n key, `params` its placeholders. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    readonly params: Record<string, unknown> = {},
  ) {
    super(`${status} ${code}`)
    this.name = 'ApiError'
  }
}

/** An `ApiError` whose code a specific field can show, else nothing (the banner takes it). */
export function claim(error: unknown, ...codes: ErrorCode[]): ApiError | undefined {
  return error instanceof ApiError && codes.includes(error.code) ? error : undefined
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface RequestOptions {
  method?: Method
  body?: unknown
  signal?: AbortSignal
}

/** The wire form of one call: JSON gets its content type, a raw blob carries its own. */
interface Payload {
  method: Method
  body?: BodyInit
  contentType?: string
  signal?: AbortSignal
}

/** `/platform/...` paths hit the platform system; everything else is restaurant. */
export function systemOf(path: string): System {
  return path === '/platform' || path.startsWith('/platform/') ? 'platform' : 'restaurant'
}

/** The auth API is mounted per system: `authPath('platform', '/login')` → `/platform/auth/login`. */
export function authPath(system: System, suffix: string) {
  return system === 'platform' ? `/platform/auth${suffix}` : `/auth${suffix}`
}

async function toError(res: Response): Promise<ApiError> {
  let body: Partial<ErrorBody> | undefined
  try {
    body = (await res.json()) as Partial<ErrorBody>
  } catch {
    // Not our envelope (proxy/gateway page): surface it as INTERNAL.
  }
  return new ApiError(res.status, body?.code ?? 'INTERNAL', body?.params ?? {})
}

async function send(path: string, payload: Payload, csrfToken: string | undefined) {
  const headers: Record<string, string> = { accept: 'application/json' }
  if (payload.contentType) headers['content-type'] = payload.contentType
  if (MUTATING.has(payload.method) && csrfToken) headers[CSRF_HEADER] = csrfToken
  return fetch(`${BASE_URL}${path}`, {
    method: payload.method,
    headers,
    credentials: 'same-origin',
    ...(payload.body !== undefined ? { body: payload.body } : {}),
    ...(payload.signal ? { signal: payload.signal } : {}),
  })
}

/**
 * Rebuild auth state + CSRF token from a live session cookie (`GET …/me`).
 * Resolves to the new state; 401 leaves the store anonymous and rethrows.
 */
export async function refreshSession<S extends System>(system: S): Promise<void> {
  const res = await send(authPath(system, '/me'), { method: 'GET' }, undefined)
  if (!res.ok) {
    const err = await toError(res)
    if (err.status === 401) clearAuth(system)
    throw err
  }
  const me = (await res.json()) as MeResponse<UserOf<S>>
  setAuthenticated(system, {
    user: me.user,
    csrfToken: me.csrf_token,
  })
}

/**
 * The rules every call follows (T15):
 * - mutating requests carry `X-CSRF-Token` when a session is known;
 * - 401 `UNAUTHORIZED` clears the system's auth state (the route guard redirects);
 *   `INVALID_CREDENTIALS` is also 401 but means "wrong password", state untouched;
 * - 403 `CSRF_MISMATCH` (token lost on reload) re-fetches `…/me` and retries once.
 */
async function exchange<T>(path: string, payload: Payload): Promise<T> {
  const system = systemOf(path)
  const token = () => {
    const auth = getAuth(system)
    return auth.status === 'authenticated' ? auth.csrfToken : undefined
  }

  let res = await send(path, payload, token())
  if (res.status === 403) {
    const err = await toError(res)
    if (err.code !== 'CSRF_MISMATCH') throw err
    await refreshSession(system)
    res = await send(path, payload, token())
  }

  if (!res.ok) {
    const err = await toError(res)
    if (err.status === 401 && err.code === 'UNAUTHORIZED') clearAuth(system)
    throw err
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T)
}

/** Typed JSON API call. Types come from `src/bindings/` (ts-rs). */
export function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
  return exchange<T>(path, {
    method: init.method ?? 'GET',
    ...(init.body !== undefined ? { body: JSON.stringify(init.body), contentType: 'application/json' } : {}),
    ...(init.signal ? { signal: init.signal } : {}),
  })
}

/** Raw-body POST (F1 images): the bytes are the message, the backend sniffs the format. */
export function upload<T>(path: string, blob: Blob, signal?: AbortSignal): Promise<T> {
  return exchange<T>(path, { method: 'POST', body: blob, ...(signal ? { signal } : {}) })
}
